import type { GuestAmenityStagingLine, GuestAmenityStagingSubitem } from "@/types/checklist";
import {
  normalizeLeaveOutRule,
  type GuestAmenityPlacement,
  type LeaveOutRule,
} from "@/lib/leaveOut";
import { planHasLeaveOut, parseLeaveOutPlan } from "@/lib/leaveOutPlan";
import { supabase } from "@/integrations/supabase/client";

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return x !== null && typeof x === "object" && !Array.isArray(x);
}

function parseSubitem(raw: unknown): GuestAmenityStagingSubitem | null {
  if (!isRecord(raw)) return null;
  const item_label = typeof raw.item_label === "string" ? raw.item_label.trim() : "";
  const target_count =
    typeof raw.target_count === "number" && Number.isFinite(raw.target_count) && raw.target_count >= 0
      ? Math.floor(raw.target_count)
      : 1;
  const id = typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : newId();
  const inventory_item_id =
    typeof raw.inventory_item_id === "string" && raw.inventory_item_id.trim()
      ? raw.inventory_item_id.trim()
      : null;
  const description = typeof raw.description === "string" ? raw.description.trim() : "";
  if (!item_label) return null;
  return { id, item_label, inventory_item_id, description: description || undefined, target_count };
}

function parsePlacement(raw: unknown): GuestAmenityPlacement | null {
  if (!isRecord(raw)) return null;
  const place_label = typeof raw.place_label === "string" ? raw.place_label.trim() : "";
  if (!place_label) return null;
  const qty =
    typeof raw.qty === "number" && Number.isFinite(raw.qty) && raw.qty >= 0
      ? Math.floor(raw.qty)
      : 1;
  const id = typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : newId();
  const property_room_id =
    typeof raw.property_room_id === "string" && raw.property_room_id.trim()
      ? raw.property_room_id.trim()
      : null;
  return { id, place_label, qty, property_room_id };
}

function parseLeaveOutRule(raw: unknown, fallbackQty: number): LeaveOutRule | null {
  if (raw == null) return null;
  return normalizeLeaveOutRule(raw, fallbackQty);
}

function parseLine(raw: unknown): GuestAmenityStagingLine | null {
  if (!isRecord(raw)) return null;
  const id = typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : newId();
  const item_label = typeof raw.item_label === "string" ? raw.item_label.trim() : "";
  if (!item_label) return null;
  const property_room_id =
    typeof raw.property_room_id === "string" && raw.property_room_id.trim()
      ? raw.property_room_id.trim()
      : null;
  const room_name_snapshot =
    typeof raw.room_name_snapshot === "string" && raw.room_name_snapshot.trim()
      ? raw.room_name_snapshot.trim()
      : null;
  const inventory_item_id =
    typeof raw.inventory_item_id === "string" && raw.inventory_item_id.trim()
      ? raw.inventory_item_id.trim()
      : null;
  const description = typeof raw.description === "string" ? raw.description.trim() : "";
  const target_count =
    typeof raw.target_count === "number" && Number.isFinite(raw.target_count) && raw.target_count >= 0
      ? Math.floor(raw.target_count)
      : 1;
  const host_reference_photo_url =
    typeof raw.host_reference_photo_url === "string" && raw.host_reference_photo_url.trim()
      ? raw.host_reference_photo_url.trim()
      : null;
  const subRaw = raw.subitems;
  const subitems: GuestAmenityStagingSubitem[] = [];
  if (Array.isArray(subRaw)) {
    for (const s of subRaw) {
      const p = parseSubitem(s);
      if (p) subitems.push(p);
    }
  }

  const leave_out_rule = parseLeaveOutRule(raw.leave_out_rule, target_count);
  const leave_out_plan = parseLeaveOutPlan(raw.leave_out_plan);
  const placements: GuestAmenityPlacement[] = [];
  if (Array.isArray(raw.placements)) {
    for (const p of raw.placements) {
      const parsed = parsePlacement(p);
      if (parsed) placements.push(parsed);
    }
  }

  return {
    id,
    property_room_id,
    room_name_snapshot,
    inventory_item_id,
    item_label,
    description,
    target_count,
    leave_out_rule: leave_out_rule ?? undefined,
    leave_out_plan: leave_out_plan ?? undefined,
    placements: placements.length > 0 ? placements : undefined,
    host_reference_photo_url,
    subitems: subitems.length > 0 ? subitems : undefined,
  };
}

/** Parse checklist `guest_amenity_staging` jsonb into normalized lines. */
export function parseGuestAmenityStagingFromChecklist(raw: unknown): GuestAmenityStagingLine[] {
  if (raw == null) return [];
  let arr: unknown[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === "string") {
    try {
      const j = JSON.parse(raw) as unknown;
      if (Array.isArray(j)) arr = j;
    } catch {
      return [];
    }
  } else return [];
  const out: GuestAmenityStagingLine[] = [];
  for (const row of arr) {
    const line = parseLine(row);
    if (line) out.push(line);
  }
  return out;
}

/** Lines the cleaner should actually set out (not empty closet rows). */
export function cleanerLeaveOutLines(lines: GuestAmenityStagingLine[]): GuestAmenityStagingLine[] {
  return lines.filter((line) => {
    if (line.leave_out_plan && planHasLeaveOut(line.leave_out_plan)) return true;
    const rule = effectiveLeaveOutRule(line);
    if (rule.qty > 0 && (line.placements?.length || line.leave_out_rule)) return true;
    return false;
  });
}

/**
 * Staging on the job checklist, or the property's default cleaning checklist.
 * Leave-out is saved from Inventory onto that default list.
 */
export async function resolveJobLeaveOutStaging(
  propertyId: string,
  attachedStaging: unknown,
): Promise<GuestAmenityStagingLine[] | null> {
  const attached = cleanerLeaveOutLines(parseGuestAmenityStagingFromChecklist(attachedStaging));
  if (attached.length > 0) return attached;

  const { data, error } = await supabase
    .from("checklists")
    .select("guest_amenity_staging, is_default, type, created_at")
    .eq("property_id", propertyId)
    .order("is_default", { ascending: false })
    .order("created_at", { ascending: true });
  if (error || !data?.length) return null;

  const cleaning = data.filter((c) => c.type === "cleaning" || !c.type);
  const target = cleaning.find((c) => c.is_default) ?? cleaning[0] ?? data[0];
  const parsed = cleanerLeaveOutLines(parseGuestAmenityStagingFromChecklist(target?.guest_amenity_staging));
  return parsed.length > 0 ? parsed : null;
}

export function newGuestAmenityStagingLine(): GuestAmenityStagingLine {
  return {
    id: newId(),
    property_room_id: null,
    room_name_snapshot: null,
    inventory_item_id: null,
    item_label: "",
    description: "",
    target_count: 1,
    leave_out_rule: { mode: "fixed", qty: 1, per_guests: 2, min_qty: null, max_qty: null },
    placements: [],
    host_reference_photo_url: null,
  };
}

/** Kit / gift basket with at least one empty part row ready to fill in. */
export function newGuestAmenityKitLine(): GuestAmenityStagingLine {
  return {
    ...newGuestAmenityStagingLine(),
    item_label: "",
    target_count: 1,
    leave_out_rule: { mode: "fixed", qty: 1, per_guests: 2, min_qty: null, max_qty: null },
    subitems: [newGuestAmenityStagingSubitem()],
  };
}

export function isGuestAmenityKitLine(line: GuestAmenityStagingLine): boolean {
  return (line.subitems?.length ?? 0) > 0;
}

export function newGuestAmenityStagingSubitem(): GuestAmenityStagingSubitem {
  return { id: newId(), item_label: "", inventory_item_id: null, target_count: 1 };
}

export function newGuestAmenityPlacement(place_label = "", qty = 1): GuestAmenityPlacement {
  return { id: newId(), place_label, qty: Math.max(0, Math.floor(qty)), property_room_id: null };
}

/** Effective leave-out rule for a line (legacy lines → fixed from target_count). */
export function effectiveLeaveOutRule(line: GuestAmenityStagingLine): LeaveOutRule {
  if (line.leave_out_rule) return normalizeLeaveOutRule(line.leave_out_rule, line.target_count);
  return { mode: "fixed", qty: Math.max(0, Math.floor(line.target_count)), per_guests: 2 };
}
